import type { BlotterTableFilter, BlotterTableStorageContextProps, ExtraGridState } from '@talos/kyoko';
import type { GridState } from 'ag-grid-community';
import { cloneDeep } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { LayoutPanelApi } from '../../../types';
import type { PanelBlottersState } from './panelPersistence';

/**
 * Persists blotter table state in dockview panel params instead of app-wide blotters3 storage.
 */
export function usePanelBlotterTableStorage(
  persistedBlotterTableStates: PanelBlottersState | undefined,
  api: LayoutPanelApi
): BlotterTableStorageContextProps {
  const blotterTableStatesRef = useRef<PanelBlottersState | undefined>(persistedBlotterTableStates);
  useEffect(() => {
    blotterTableStatesRef.current = persistedBlotterTableStates;
  }, [persistedBlotterTableStates]);

  const updateBlotterTableStates = useCallback(
    (mutator: (state: PanelBlottersState) => PanelBlottersState) => {
      const currentState = blotterTableStatesRef.current ?? {};
      const nextState = mutator(currentState);
      if (nextState === currentState) {
        return;
      }
      blotterTableStatesRef.current = nextState;
      api.updateParameters({ blotterTablesState: nextState });
    },
    [api]
  );

  const getState = useCallback<BlotterTableStorageContextProps['getState']>(blotterID => {
    return blotterTableStatesRef.current?.[blotterID];
  }, []);

  const setGridState = useCallback(
    (blotterID: string, gridState: GridState | undefined) => {
      updateBlotterTableStates(state => ({
        ...state,
        [blotterID]: {
          ...(state[blotterID] ?? {}),
          gridState,
        },
      }));
    },
    [updateBlotterTableStates]
  );

  const setFilterState = useCallback(
    (blotterID: string, filter: BlotterTableFilter | undefined) => {
      updateBlotterTableStates(state => ({
        ...state,
        [blotterID]: {
          ...(state[blotterID] ?? {}),
          filter,
        },
      }));
    },
    [updateBlotterTableStates]
  );

  const setExtraGridState = useCallback(
    (blotterID: string, extraGridState: ExtraGridState | undefined) => {
      updateBlotterTableStates(state => ({
        ...state,
        [blotterID]: {
          ...(state[blotterID] ?? {}),
          extraGridState,
        },
      }));
    },
    [updateBlotterTableStates]
  );

  const cloneState = useCallback(
    (fromBlotterID: string, toBlotterID: string) => {
      updateBlotterTableStates(state => {
        if (!state[fromBlotterID]) {
          return state;
        }
        return {
          ...state,
          [toBlotterID]: cloneDeep(state[fromBlotterID]),
        };
      });
    },
    [updateBlotterTableStates]
  );

  const clearAllBlotterState = useCallback(() => {
    blotterTableStatesRef.current = {};
    api.updateParameters({ blotterTablesState: {} });
  }, [api]);

  return useMemo(
    () => ({
      getState,
      setGridState,
      setFilterState,
      setExtraGridState,
      cloneState,
      clearAllBlotterState,
    }),
    [getState, setGridState, setFilterState, setExtraGridState, cloneState, clearAllBlotterState]
  );
}
